import type { MapDefinition } from '../game/model/types'
import { MAP_SCHEMA_VERSION, parseImportedMapJson } from './mapIo'

const STORAGE_KEY = 'shapes-td.map-drafts.v1'

export interface MapDraftRecord {
  id: string
  name: string
  updatedAt: string
  map: MapDefinition
}

export interface DraftStore {
  listDrafts: () => MapDraftRecord[]
  getDraft: (id: string) => MapDraftRecord | undefined
  saveDraft: (map: MapDefinition) => MapDraftRecord
  deleteDraft: (id: string) => boolean
}

function readAll(storage: Storage): MapDraftRecord[] {
  const raw = storage.getItem(STORAGE_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isDraftRecord)
  } catch {
    return []
  }
}

function isDraftRecord(value: unknown): value is MapDraftRecord {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<MapDraftRecord>
  return (
    typeof record.id === 'string' &&
    typeof record.name === 'string' &&
    typeof record.updatedAt === 'string' &&
    !!record.map &&
    typeof record.map === 'object'
  )
}

function writeAll(storage: Storage, drafts: MapDraftRecord[]): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(drafts))
}

export function createDraftStore(storage?: Storage): DraftStore {
  const resolveStorage = (): Storage => {
    if (storage) return storage
    if (typeof localStorage === 'undefined') {
      throw new Error('localStorage is not available')
    }
    return localStorage
  }

  return {
    listDrafts: () => readAll(resolveStorage()).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),

    getDraft: (id) => readAll(resolveStorage()).find((d) => d.id === id),

    saveDraft: (map) => {
      const backend = resolveStorage()
      const normalized: MapDefinition = {
        ...structuredClone(map),
        schemaVersion: map.schemaVersion ?? MAP_SCHEMA_VERSION
      }
      const check = parseImportedMapJson(JSON.stringify(normalized))
      if (!check.ok) {
        throw new Error(check.errors.join('; '))
      }
      const record: MapDraftRecord = {
        id: normalized.id,
        name: normalized.name,
        updatedAt: new Date().toISOString(),
        map: check.map
      }
      const drafts = readAll(backend).filter((d) => d.id !== record.id)
      drafts.push(record)
      writeAll(backend, drafts)
      return record
    },

    deleteDraft: (id) => {
      const backend = resolveStorage()
      const drafts = readAll(backend)
      const next = drafts.filter((d) => d.id !== id)
      if (next.length === drafts.length) return false
      writeAll(backend, next)
      return true
    }
  }
}

export const mapDraftStore: DraftStore = {
  listDrafts: () => createDraftStore().listDrafts(),
  getDraft: (id) => createDraftStore().getDraft(id),
  saveDraft: (map) => createDraftStore().saveDraft(map),
  deleteDraft: (id) => createDraftStore().deleteDraft(id)
}